import { useEffect, useRef, useState } from 'react';
import { useOnboardingStore } from '../../../store/onboardingStore';
import { useAuth } from '../../../context/AuthContext';
import { dictionary } from '../../../utils/dictionary';
import { Image, Layers, MessageSquarePlus, Megaphone } from 'lucide-react';
import { ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL } from '../../../const/onboardingVideoReviews';

export function StepFunnelSummary() {
  const { language } = useAuth();
  const { multiFunnelData, data, fetchEstimatedCost, updateAdditionalUserInstructions, adTemplates, estimatedCost } = useOnboardingStore();
  const { funnels } = multiFunnelData;

  // Fix #6 (2026-05-19): textarea is auto-opened only when the user already typed
  // something (e.g. when coming back to this step). Default-closed avoids overwhelming
  // the user on first arrival.
  const [isContextOpen, setIsContextOpen] = useState<boolean>(() =>
    Boolean(data.additionalUserInstructions && data.additionalUserInstructions.trim().length > 0)
  );
  const contextTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const selectedAdIds = Array.from(new Set(funnels.flatMap((f) => f.selectedAdTemplates || [])));
  const selectedAds = selectedAdIds.map((id) => adTemplates.find((template) => template.id === id)?.name || id);
  const funnelAngles = funnels.map((funnel, index) =>
    funnel.selectedAngles[0]?.name || `${dictionary["funnels-label"]?.[language] ?? "Embudo"} ${index + 1}`
  );

  useEffect(() => {
    fetchEstimatedCost();
  }, []);

  useEffect(() => {
    const textarea = contextTextareaRef.current;
    if (!isContextOpen || !textarea) return;

    if (!data.additionalUserInstructions?.trim()) {
      textarea.style.height = '40px';
      textarea.style.overflowY = 'hidden';
      return;
    }

    textarea.style.height = 'auto';
    const nextHeight = Math.min(textarea.scrollHeight, 132);
    textarea.style.height = `${nextHeight}px`;
    textarea.style.overflowY = textarea.scrollHeight > 132 ? 'auto' : 'hidden';
  }, [data.additionalUserInstructions, isContextOpen]);

  return (
    <div className="space-y-6">
      <div className="text-center mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1">
          {dictionary["all-ready-to-create"]?.[language] ?? "Todo listo para crear"}
        </h1>
        <p className="text-sm text-[#64748B]">
          {dictionary["review-summary"]?.[language] ?? "Revisa el resumen de lo que Fluxi generará para ti"}
        </p>
      </div>

      <div className="max-w-lg mx-auto space-y-3">
        <FunnelSummaryBlock
          angles={funnelAngles}
          funnels={funnels}
          estimatedCost={estimatedCost}
        />

        {selectedAds.length > 0 && (
          <SummaryReviewBlock
            icon={Image}
            title={`${dictionary["ads-count-label"]?.[language] ?? "Anuncios"}: ${selectedAds.length}`}
            items={selectedAds}
          />
        )}
      </div>

      <div className="max-w-lg mx-auto">
        {!isContextOpen ? (
          <button
            type="button"
            onClick={() => setIsContextOpen(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-2xl border border-dashed border-[#CBD5E1] text-[#64748B] hover:bg-[#F8FAFC] hover:border-[#94A3B8] hover:text-[#334155] transition-colors text-sm font-semibold"
          >
            <MessageSquarePlus className="w-4 h-4" />
            {dictionary["add-context-suggestion"]?.[language] ?? "Agregar sugerencias de comunicación (Opcional)"}
          </button>
        ) : (
          <div className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-xs font-semibold text-[#475569]">
                <Megaphone className="w-4 h-4 text-[#64748B]" />
                {dictionary["context-suggestion-label"]?.[language] ?? "Contexto extra para la IA"}
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsContextOpen(false);
                  if (!data.additionalUserInstructions?.trim()) {
                    updateAdditionalUserInstructions('');
                  }
                }}
                className="text-[#94A3B8] hover:text-[#475569] text-xs"
              >
                {dictionary["close-context"]?.[language] ?? "Cerrar"}
              </button>
            </div>
            <textarea
              ref={contextTextareaRef}
              value={data.additionalUserInstructions ?? ''}
              onChange={(e) => updateAdditionalUserInstructions(e.target.value)}
              placeholder={dictionary["context-suggestion-placeholder"]?.[language] ?? "Ej.: tono más directo, evitar claims médicos."}
              rows={1}
              maxLength={2000}
              className="max-h-[132px] min-h-[40px] w-full resize-none rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm leading-5 focus:border-[#3B4A9F] focus:outline-none focus:ring-1 focus:ring-[#3B4A9F] placeholder:text-gray-400"
            />
            <p className="text-[11px] text-gray-400">
              {dictionary["context-suggestion-helper"]?.[language] ?? "Este texto se usará como contexto adicional en cada generación (HTML e imágenes)."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelSummaryBlock({
  angles,
  funnels,
  estimatedCost,
}: {
  angles: string[];
  funnels: Array<{ includeVideoReviewsGifs?: boolean }>;
  estimatedCost: {
    funnels_cost: number;
    video_reviews_gifs_credits_per_funnel?: number;
  } | null;
}) {
  const total = funnels.length;
  const baseCreditsPerFunnel = Math.max(
    0,
    Math.round((estimatedCost?.funnels_cost ?? total * 85) / Math.max(total, 1))
  );
  const gifsCreditsPerFunnel =
    estimatedCost?.video_reviews_gifs_credits_per_funnel ??
    ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 rounded-2xl border border-[#DCE3EC] bg-[#E9EEF4] px-4 py-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#475569] ring-1 ring-[#E2E8F0]">
          <Layers className="h-[18px] w-[18px]" />
        </span>
        <p className="text-sm font-black text-gray-900">TOTAL DE EMBUDOS: {total}</p>
      </div>

      <div className="space-y-2">
        {angles.map((angle, index) => {
          const label = `Ángulo ${String.fromCharCode(65 + index)}:`;
          const hasGifs = Boolean(funnels[index]?.includeVideoReviewsGifs);
          const creditsLabel = hasGifs
            ? `Embudo ${index + 1}: ${baseCreditsPerFunnel} créditos + Gifs: ${gifsCreditsPerFunnel} créditos`
            : `Embudo ${index + 1}: ${baseCreditsPerFunnel} créditos`;

          return (
            <div key={`${angle}-${index}`} className="rounded-2xl border border-[#E2E8F0] bg-[#F7F8FA] px-4 py-3">
              <p className="mb-1 text-[11px] font-black tracking-wide text-[#94A3B8]">{creditsLabel}</p>
              <p className="text-sm font-semibold leading-relaxed text-[#475569]">
                <span className="font-black text-[#334155]">{label}</span> {angle}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SummaryReviewBlock({
  icon: Icon,
  title,
  items,
}: {
  icon: typeof Layers;
  title: string;
  items: string[];
}) {
  return (
    <div className="rounded-2xl border border-[#E2E8F0] bg-[#F3F5F8] px-4 py-4">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-[#475569] ring-1 ring-[#E2E8F0]">
          <Icon className="h-[18px] w-[18px]" />
        </span>
        <p className="text-sm font-black text-gray-900">{title}</p>
      </div>
      <div className="mt-3 pl-12">
        {items.map((item, index) => (
          <div key={`${item}-${index}`}>
            {index > 0 && <div className="mx-auto my-2.5 h-px w-[86%] bg-[#DDE2E8]" />}
            <p className="text-sm font-semibold text-[#475569]">
            {item}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}
